'use client';

import { faArrowUp, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@ui';
import { StyledInput } from '@ui/Form';
import { useClubMemberFields } from 'components/ClubMemberManagement/hooks';
import { TableFieldAssistant } from 'components/FormFields/TableFieldAssistant';
import { CategoryDiscountsEditor } from 'components/Trainingsjournal/CategoryDiscountsInput';
import { range } from 'lodash';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useState } from 'react';
import { v4 as uuid } from 'uuid';
import { WYSIWYG } from '../forms/WYSIWYG';
import { AutoFillSelect, FieldOptions, FieldOptionsResult } from './FieldOptions';
import { CategoryDiscountRule, FormField, PaymentOption } from './FormAssistent.types';

type AddFieldFn = (fields: FormField | FormField[], checkRestricted?: boolean) => void;

const INPUT_CLS =
    'tw-w-full tw-border tw-border-solid tw-border-accents-3 tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm tw-bg-accents-0 focus:tw-outline-none focus:tw-border-success tw-transition-colors';

// ── FieldTypeInfo ──────────────────────────────────────────────────────────────

export const FieldTypeInfo: React.FC<{ hasTrainingplanFields: boolean }> = ({ hasTrainingplanFields }) => (
    <div className="tw-text-center">
        <FontAwesomeIcon icon={faArrowUp} />
        <p className="tw-mt-2">Bitte wähle ein Typ aus um ein Feld zu deinem Formular hinzuzufügen!</p>
        {!hasTrainingplanFields && (
            <p className="tw-text-sm tw-text-accents-6">
                Den Feldtyp <b>Vorraussetzungen für den Trainingsplan</b> solltest du hinzufügen, falls du unsere{' '}
                <Link href="/trainingsplanerstellung">Trainingsplan Software</Link> gebucht hast und direkt aus deinen
                Anmeldungen den passenden Trainingsplan generieren möchtest.
            </p>
        )}
    </div>
);

// ── LegalOptions ───────────────────────────────────────────────────────────────

export const LegalOptions: React.FC<{ cb: AddFieldFn; legalPrefix?: string }> = ({ cb, legalPrefix = 'legal_' }) => {
    const { query } = useRouter();
    const linkUrl = `${process.env.NEXT_PUBLIC_HOST}club/${query.slug}?tab=`;

    const handleAdd = () =>
        cb([
            {
                type: 'checkbox',
                name: `${legalPrefix}agb`,
                rules: ['required'],
                label: 'Ich akzeptiere die AGB, welche unter folgendem Link zu finden sind.',
                link: `${linkUrl}agb`
            },
            {
                type: 'checkbox',
                name: `${legalPrefix}datenschutz`,
                rules: ['required'],
                label: 'Ich akzeptiere die Datenschutzerklärung, welche unter folgendem Link zu finden ist.',
                link: `${linkUrl}datenschutz`
            },
            {
                type: 'checkbox',
                name: `${legalPrefix}widerrufsbelehrung`,
                rules: ['required'],
                label: 'Ich akzeptiere die Widerrufsbelehrung, welche unter folgendem Link zu finden ist.',
                link: `${linkUrl}widerrufsbelehrung`
            }
        ]);

    return (
        <>
            <p className="tw-text-center tw-text-sm">
                Hier helfen wir dir dein Formular Rechtssicher zu machen und verlinken automatisch deine im Club
                hinterlegten AGB, Datenschutzerklärung, Widerrufsbelehrung und Impressum.
            </p>
            <Button color="default" block onPress={handleAdd} className="tw-mt-5">
                Automatisch hinzufügen
            </Button>
        </>
    );
};

// ── UserInvoicesOptions ────────────────────────────────────────────────────────

const INVOICE_FIELDS: Record<string, string> = {
    line0: 'Rechnungsempfänger',
    line1: 'Straße und Hausnummer',
    line2: 'Adresszusatz',
    city: 'Ort',
    postcode: 'Postleitzahl',
    iban: 'IBAN'
};

const getInvoiceRules = (key: string): string[] =>
    ['line0', 'line1', 'city', 'postcode'].includes(key) ? ['required'] : [];

export const UserInvoicesOptions: React.FC<{ cb: AddFieldFn; invoicePrefix: string }> = ({ cb, invoicePrefix }) => (
    <>
        <h1 className="tw-mb-3 tw-text-xl tw-font-semibold">Rechnungen</h1>
        <Button
            color="default"
            onPress={() =>
                cb(
                    Object.entries(INVOICE_FIELDS).map(([key, label]) => ({
                        type: key === 'iban' ? 'iban' : 'text',
                        placeholder: '',
                        name: `${invoicePrefix}${key}`,
                        rules: getInvoiceRules(key),
                        label
                    })),
                    false
                )
            }
        >
            Hinzufügen
        </Button>
    </>
);

// ── TrainingplanOptions ────────────────────────────────────────────────────────

interface TrainingplanValues {
    startTime: string;
    endTime: string;
    disabledCells: null;
    period: string;
    weekdays: string[];
    rows: any[];
    minTimes: number;
    minDays: number;
    minTrainingscount: number;
    maxTrainingscount: number;
    nameTitle: string;
    ageTitle: string;
    trainingscountTitle: string;
    genderTitle: string;
    remarksTitle: string;
    tableTitle: string;
}

const TRAININGPLAN_DEFAULTS: TrainingplanValues = {
    startTime: '00:00',
    endTime: '00:00',
    disabledCells: null,
    period: '1',
    weekdays: [],
    rows: [],
    minTimes: 1,
    minDays: 1,
    minTrainingscount: 1,
    maxTrainingscount: 3,
    nameTitle: 'Vorname, Nachname (Trainierende*r)',
    ageTitle: 'Geburtsdatum',
    trainingscountTitle: 'Wie oft möchtest du pro Woche trainieren?',
    genderTitle: 'Geschlecht',
    remarksTitle: 'Bemerkungen',
    tableTitle: 'Wann kannst du trainieren? (bitte alle möglichen Zeiten angeben)'
};

export const TrainingplanOptions: React.FC<{ cb: AddFieldFn; tpPrefix?: string }> = ({
    cb,
    tpPrefix = 'trainingplan_'
}) => {
    const [values, setValues] = useState<TrainingplanValues>(TRAININGPLAN_DEFAULTS);

    const handleSubmit = () => {
        if (values.startTime.split(':')[0] < values.endTime.split(':')[0] && values.weekdays.length > 0) {
            cb(
                [
                    {
                        type: 'date',
                        placeholder: '',
                        name: `${tpPrefix}birthdate`,
                        rules: ['required'],
                        label: values.ageTitle
                    },
                    {
                        type: 'select',
                        name: `${tpPrefix}trainingscount`,
                        rules: ['required'],
                        label: values.trainingscountTitle,
                        options: range(Number(values.minTrainingscount), Number(values.maxTrainingscount) + 1).map(
                            String
                        )
                    },
                    {
                        type: 'select',
                        name: `${tpPrefix}gender`,
                        rules: ['required'],
                        label: values.genderTitle,
                        options: ['männlich', 'weiblich', 'divers']
                    },
                    {
                        type: 'table',
                        rows: values.rows,
                        columns: values.weekdays,
                        disabledTimes: (values as any).disabledTimes,
                        minTimes: values.minTimes,
                        minDays: values.minDays,
                        name: `${tpPrefix}table`,
                        rules: ['required', `min:${values.minTimes}`],
                        label: values.tableTitle
                    },
                    { type: 'text', placeholder: '', name: `${tpPrefix}remarks`, rules: [], label: values.remarksTitle }
                ],
                false
            );
        } else {
            alert(
                'Deine Startzeit darf nicht gleich mit deiner Endzeit sein oder vor ihr liegen, darüber hinaus musst du mindetstens einen Wochentag ausgewählt haben!'
            );
        }
    };

    return (
        <>
            <p className="tw-text-center tw-text-sm">
                Hier helfen wir dir dein Formular so zu konfigurieren, dass du unsere{' '}
                <Link href="/trainingsplanerstellung">Trainingsplan Software</Link> optimal nutzen kannst.
            </p>
            <div className="tw-mb-2 tw-flex tw-items-center">
                <h5 className="tw-mb-0 tw-mr-2 tw-font-semibold">Trainingszeiten</h5>
            </div>
            <label className="tw-mb-2 tw-block tw-text-sm tw-font-medium">Trainingshäufigkeit einstellen</label>
            <div className="tw-mb-4 tw-grid tw-grid-cols-2 tw-gap-4">
                <div className="tw-flex tw-flex-col tw-gap-1">
                    <label className="tw-text-sm">Minimale Trainingshäufigkeit</label>
                    <input
                        type="number"
                        min={1}
                        max={7}
                        className={INPUT_CLS}
                        value={values.minTrainingscount}
                        onChange={(e) => setValues((v) => ({ ...v, minTrainingscount: Number(e.target.value) }))}
                    />
                </div>
                <div className="tw-flex tw-flex-col tw-gap-1">
                    <label className="tw-text-sm">Maximale Trainingshäufigkeit</label>
                    <input
                        type="number"
                        min={values.minTrainingscount}
                        max={7}
                        className={INPUT_CLS}
                        value={values.maxTrainingscount}
                        onChange={(e) => setValues((v) => ({ ...v, maxTrainingscount: Number(e.target.value) }))}
                    />
                </div>
            </div>
            <label className="tw-mb-2 tw-block tw-text-sm tw-font-medium">Spalten und Reihen hinzufügen</label>
            <TableFieldAssistant values={values} setValues={setValues} />
            <Button
                color="default"
                isDisabled={values.weekdays.length <= 0 || !values.tableTitle}
                onPress={handleSubmit}
            >
                Abschließen
            </Button>
        </>
    );
};

// ── TableOptions ───────────────────────────────────────────────────────────────

interface TableValues {
    startTime: string;
    endTime: string;
    weekdays: string[];
    period: number;
    rows: any[];
    disabledTimes: any[];
    minTimes: number;
    minDays: number;
}

const TABLE_DEFAULTS: TableValues = {
    startTime: '00:00',
    endTime: '00:00',
    weekdays: [],
    period: 1,
    rows: [],
    disabledTimes: [],
    minTimes: 1,
    minDays: 1
};

export const TableOptions: React.FC<{ cb: AddFieldFn }> = ({ cb }) => {
    const [values, setValues] = useState<TableValues>(TABLE_DEFAULTS);

    const handleConfirm = (val: FieldOptionsResult) => {
        if (values.startTime.split(':')[0] < values.endTime.split(':')[0] && values.weekdays.length > 0) {
            cb({
                ...val,
                type: 'table',
                rows: values.rows,
                columns: values.weekdays,
                disabledTimes: values.disabledTimes,
                minTimes: values.minTimes,
                minDays: values.minDays,
                rules: [...val.rules, `min:${values.minTimes}`]
            });
            setValues(TABLE_DEFAULTS);
        } else {
            alert(
                'Deine Startzeit darf nicht gleich mit deiner Endzeit sein oder vor ihr liegen, darüber hinaus musst du mindetstens einen Wochentag ausgewählt haben!'
            );
        }
    };

    return (
        <FieldOptions
            nameLabel="Tabellentitel"
            placeholder="Trainingstage und Zeiten"
            buttonLabel="Tabelle erstellen"
            availableRules={['required']}
            cb={handleConfirm}
        >
            <TableFieldAssistant values={values} setValues={setValues} />
        </FieldOptions>
    );
};

// ── TextFieldOptions ───────────────────────────────────────────────────────────

export const TextFieldOptions: React.FC<{ cb: AddFieldFn }> = ({ cb }) => {
    const { fields, isLoading } = useClubMemberFields();
    const [placeholder, setPlaceholder] = useState('');
    const [autofill, setAutofill] = useState('');
    const selectableFields = fields?.filter(
        (field): field is { key: string; title: string } =>
            typeof field.key === 'string' && typeof field.title === 'string'
    );

    return (
        <FieldOptions
            nameLabel="Textfeldtitel"
            placeholder="z. B. Name"
            buttonLabel="Textfeld erstellen"
            availableRules={['required']}
            cb={(val) => {
                cb({ type: 'text', placeholder, autofill, ...val });
                setPlaceholder('');
                setAutofill('');
            }}
        >
            <StyledInput
                label="Platzhaltertext"
                name="placeholder"
                type="text"
                value={placeholder}
                bordered
                rounded
                fullWidth
                onChange={(e) => setPlaceholder((e.target as HTMLInputElement).value)}
            />
            <div className="tw-mb-4">
                <label className="tw-mb-1 tw-block tw-text-sm tw-font-medium">Mit Mitgliedsdaten verknüpfen</label>
                <AutoFillSelect
                    value={autofill}
                    onChange={setAutofill}
                    fields={selectableFields}
                    isLoading={isLoading}
                />
            </div>
        </FieldOptions>
    );
};

// ── SectionOptions ────────────────────────────────────────────────────────────

export const SectionOptions: React.FC<{ cb: AddFieldFn }> = ({ cb }) => (
    <>
        <p className="tw-mb-4 tw-text-center tw-text-sm tw-text-accents-6">
            Abschnitte strukturieren dein Formular in klare Schritte. Sie können auch als bedingte Bereiche genutzt
            werden.
        </p>
        <Button
            color="default"
            block
            onPress={() =>
                cb({
                    type: 'section',
                    name: uuid(),
                    label: 'Neuer Abschnitt',
                    helperText: '',
                    rules: [],
                    sectionMode: 'default',
                    children: [
                        {
                            id: uuid(),
                            label: 'Schritt 1',
                            helperText: '',
                            fields: []
                        }
                    ]
                })
            }
        >
            Abschnitt hinzufügen
        </Button>
    </>
);

// ── NumberFieldOptions ─────────────────────────────────────────────────────────

export const NumberFieldOptions: React.FC<{ cb: AddFieldFn }> = ({ cb }) => {
    const [placeholder, setPlaceholder] = useState('');

    return (
        <FieldOptions
            nameLabel="Zahlenfeldtitel"
            placeholder="z. B. Alter"
            buttonLabel="Zahlenfeld erstellen"
            availableRules={['required']}
            cb={(val) => {
                cb({ type: 'number', placeholder, ...val });
                setPlaceholder('');
            }}
        >
            <StyledInput
                label="Platzhaltertext (optional)"
                name="placeholder"
                type="text"
                value={placeholder}
                bordered
                rounded
                fullWidth
                onChange={(e) => setPlaceholder((e.target as HTMLInputElement).value)}
            />
        </FieldOptions>
    );
};

// ── DateFieldOptions ───────────────────────────────────────────────────────────

export const DateFieldOptions: React.FC<{ cb: AddFieldFn }> = ({ cb }) => (
    <FieldOptions
        nameLabel="Datumsfeldtitel"
        placeholder="z. B. Geburtsdatum"
        buttonLabel="Datumsfeld erstellen"
        availableRules={['required']}
        cb={(val) => cb({ type: 'date', ...val })}
    />
);

// ── IBANFieldOptions ───────────────────────────────────────────────────────────

export const IBANFieldOptions: React.FC<{ cb: AddFieldFn }> = ({ cb }) => {
    const [placeholder, setPlaceholder] = useState('');

    return (
        <>
            <p className="tw-mb-3 tw-text-center tw-text-sm tw-text-accents-5">
                Dies ist ein spezielles Textfeld welches den Inhalt als IBAN interpretiert und mit den aktuellen
                Standards validiert.
            </p>
            <FieldOptions
                nameLabel="IBAN Feld Titel"
                placeholder="z. B. Name"
                buttonLabel="IBAN Feld erstellen"
                availableRules={['required']}
                cb={(val) => {
                    cb({ type: 'iban', placeholder, ...val });
                    setPlaceholder('');
                }}
            >
                <StyledInput
                    label="Platzhaltertext"
                    name="placeholder"
                    type="text"
                    value={placeholder}
                    bordered
                    rounded
                    fullWidth
                    onChange={(e) => setPlaceholder((e.target as HTMLInputElement).value)}
                />
            </FieldOptions>
        </>
    );
};

// ── TextOptions (WYSIWYG) ──────────────────────────────────────────────────────

export const TextOptions: React.FC<{ cb: AddFieldFn }> = ({ cb }) => {
    const [value, setValue] = useState('');

    return (
        <>
            <WYSIWYG value={value} onChange={setValue} />
            <div className="tw-mt-3 tw-flex tw-justify-end">
                <Button
                    color="default"
                    onPress={() => cb({ name: uuid(), type: 'wysiwyg', text: value, label: '', rules: [] })}
                >
                    Text einfügen
                </Button>
            </div>
        </>
    );
};

// ── CheckboxOptions ────────────────────────────────────────────────────────────

export const CheckboxOptions: React.FC<{ cb: AddFieldFn }> = ({ cb }) => {
    const [link, setLink] = useState('');

    return (
        <FieldOptions
            nameLabel="Ankreuzfeldtitel"
            placeholder="z. B. Einzel- / Gruppenstunde"
            buttonLabel="Ankreuzfeld erstellen"
            availableRules={['required']}
            textarea
            cb={(val) => cb({ type: 'checkbox', link, ...val })}
        >
            <StyledInput
                label="Link (optional)"
                name="link"
                type="text"
                placeholder="http://example.de"
                value={link}
                bordered
                rounded
                fullWidth
                onChange={(e) => setLink((e.target as HTMLInputElement).value)}
            />
        </FieldOptions>
    );
};

// ── SelectOptions ──────────────────────────────────────────────────────────────

interface SelectOptionsProps {
    cb: AddFieldFn;
    val?: string[];
}

export const SelectOptions: React.FC<SelectOptionsProps> = ({ cb, val }) => {
    const [options, setOptions] = useState<string[]>(val ?? []);
    const [optionInput, setOptionInput] = useState('');

    const addOption = (e: React.FormEvent) => {
        e.preventDefault();
        if (!optionInput) return;
        setOptions((prev) => [...prev, optionInput]);
        setOptionInput('');
    };

    const handleConfirm = (fieldVal: FieldOptionsResult) => {
        if (options.length > 0) {
            cb({ type: 'select', options, ...fieldVal } as FormField);
        } else {
            alert('Du musst mindestens eine Option hinzufügen!');
        }
    };

    return (
        <FieldOptions
            nameEditable={val === undefined}
            nameLabel="Auswahlfeldtitel"
            placeholder="z. B. Trikotgröße"
            buttonLabel={val ? 'Speichern' : 'Auswahlfeld erstellen'}
            availableRules={val ? undefined : ['required']}
            cb={handleConfirm}
        >
            <div className="tw-mb-4">
                <label className="tw-mb-1 tw-block tw-text-sm tw-font-medium">Optionen hinzufügen</label>
                <form className="tw-mb-3 tw-flex tw-gap-0" onSubmit={addOption}>
                    <input
                        placeholder="XS"
                        type="text"
                        className={`${INPUT_CLS} tw-rounded-r-none`}
                        value={optionInput}
                        onChange={(e) => setOptionInput(e.target.value)}
                    />
                    <Button
                        color="default"
                        onPress={() => addOption({ preventDefault: () => {} } as any)}
                        className="tw-rounded-l-none"
                    >
                        <FontAwesomeIcon icon={faPlus} />
                    </Button>
                </form>
                <ul className="tw-divide-y tw-divide-accents-2 tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-accents-2">
                    {options.map((opt, index) => (
                        <li key={index} className="tw-flex tw-items-center tw-gap-3 tw-px-3 tw-py-2">
                            <input
                                className={INPUT_CLS}
                                value={opt}
                                onChange={(e) =>
                                    setOptions((prev) => prev.map((o, i) => (i === index ? e.target.value : o)))
                                }
                            />
                            <Button
                                color="error"
                                size="xs"
                                onPress={() => setOptions((prev) => prev.filter((_, i) => i !== index))}
                            >
                                <FontAwesomeIcon icon={faTrash} />
                            </Button>
                        </li>
                    ))}
                </ul>
            </div>
        </FieldOptions>
    );
};

// ── PaymentOptionsField ────────────────────────────────────────────────────────

interface PaymentOptionsFieldProps {
    cb: AddFieldFn;
    val?: Partial<FormField>;
}

const DEFAULT_PAYMENT_OPTION: PaymentOption = { option: '', price: 9.99 };

export const PaymentOptionsField: React.FC<PaymentOptionsFieldProps> = ({ cb, val }) => {
    const [options, setOptions] = useState<PaymentOption[]>((val?.options as PaymentOption[]) ?? []);
    const [categoryDiscounts, setCategoryDiscounts] = useState<CategoryDiscountRule[]>(val?.category_discounts ?? []);
    const [newOption, setNewOption] = useState<PaymentOption>(DEFAULT_PAYMENT_OPTION);

    const addOption = (e: React.FormEvent) => {
        e.preventDefault();
        if (!newOption.option) return;
        setOptions((prev) => [...prev, newOption]);
        setNewOption(DEFAULT_PAYMENT_OPTION);
    };

    const updateOption = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const { name, value } = e.target;
        setOptions((prev) =>
            prev.map((opt, i) => (i === index ? { ...opt, [name]: name === 'price' ? Number(value) : value } : opt))
        );
    };

    const handleConfirm = (fieldVal: FieldOptionsResult) => {
        if (options.length > 0) {
            cb({
                type: 'payment_options',
                options,
                category_discounts: categoryDiscounts,
                ...fieldVal
            } as FormField);
        } else {
            alert('Du musst mindestens eine Option hinzufügen!');
        }
    };

    return (
        <FieldOptions
            nameEditable={val === undefined}
            nameLabel="Zahlungsoptionenfeld Titel"
            placeholder="z. B. Vereinsrang"
            buttonLabel={val ? 'Speichern' : 'Zahlungsoptionen erstellen'}
            availableRules={val ? undefined : ['required']}
            cb={handleConfirm}
        >
            <div className="tw-mb-4">
                <label className="tw-mb-1 tw-block tw-text-sm tw-font-medium">Optionen hinzufügen</label>
                <form className="tw-mb-3 tw-flex tw-gap-0" onSubmit={addOption}>
                    <input
                        placeholder="Mitglied"
                        type="text"
                        className={`${INPUT_CLS} tw-rounded-r-none`}
                        name="option"
                        value={newOption.option}
                        onChange={(e) => setNewOption((prev) => ({ ...prev, option: e.target.value }))}
                    />
                    <input
                        placeholder="10.00"
                        type="number"
                        step={0.01}
                        min={0}
                        max={10000}
                        className={`${INPUT_CLS} tw-rounded-none tw-border-l-0`}
                        name="price"
                        value={newOption.price}
                        onChange={(e) => setNewOption((prev) => ({ ...prev, price: Number(e.target.value) }))}
                    />
                    <Button
                        color="default"
                        onPress={() => addOption({ preventDefault: () => {} } as any)}
                        className="tw-rounded-l-none"
                    >
                        <FontAwesomeIcon icon={faPlus} />
                    </Button>
                </form>
                <ul className="tw-divide-y tw-divide-accents-2 tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-accents-2">
                    {options.map((opt, index) => (
                        <li key={index} className="tw-flex tw-items-center tw-gap-3 tw-px-3 tw-py-2">
                            <div className="tw-flex tw-flex-1 tw-gap-3">
                                <input
                                    className={INPUT_CLS}
                                    name="option"
                                    value={opt.option}
                                    onChange={(e) => updateOption(e, index)}
                                />
                                <input
                                    type="number"
                                    step={0.01}
                                    min={0}
                                    max={10000}
                                    className={INPUT_CLS}
                                    name="price"
                                    value={opt.price}
                                    onChange={(e) => updateOption(e, index)}
                                />
                            </div>
                            <Button
                                color="error"
                                size="xs"
                                onPress={() => setOptions((prev) => prev.filter((_, i) => i !== index))}
                            >
                                <FontAwesomeIcon icon={faTrash} />
                            </Button>
                        </li>
                    ))}
                </ul>
            </div>
            <div className="tw-mb-4">
                <CategoryDiscountsEditor fixedOnly value={categoryDiscounts} onChange={setCategoryDiscounts} />
            </div>
        </FieldOptions>
    );
};
